import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Icon, Badge, Rating, Button, Skeleton, EmptyState } from '../../components/ui';
import { fetchAllPandits } from '../../services/epujaService';

const SPECIALIZATION_LABELS = {
  'vedic-rituals': 'Vedic Rituals',
  'havan-specialist': 'Havan Specialist',
  astrology: 'Astrology',
  vastu: 'Vastu',
  'katha-vachak': 'Katha Vachak',
  'bhajan-mandali': 'Bhajan Mandali',
  'marriage-specialist': 'Marriage Specialist',
  'pitru-karma': 'Pitru Karma',
  general: 'General',
};

const SAMPLE_PANDITS = [
  { id: 'p1', name: 'Pt. Ramesh Sharma', nameHindi: 'पं. रमेश शर्मा', specialization: 'vedic-rituals', bio: 'Expert in Vedic rituals with over 20 years of experience performing sacred ceremonies at Ram Janmabhoomi and major temples across Ayodhya.', rating: 4.9, totalReviews: 312, experienceYears: 22, languages: ['Hindi', 'Sanskrit', 'English'], availableOnline: true, availableOffline: true, featured: true, isVerified: true, hourlyRate: 1500, totalPujasPerformed: 4200 },
  { id: 'p2', name: 'Pt. Suresh Tiwari', nameHindi: 'पं. सुरेश तिवारी', specialization: 'havan-specialist', bio: 'Specialises in Havan and Yagya ceremonies. Trained at Kashi Vidyapeeth with deep knowledge of Agni rituals and Vedic chanting.', rating: 4.8, totalReviews: 198, experienceYears: 18, languages: ['Hindi', 'Sanskrit'], availableOnline: false, availableOffline: true, featured: true, isVerified: true, hourlyRate: 1200, totalPujasPerformed: 2800 },
  { id: 'p3', name: 'Pt. Anand Mishra', nameHindi: 'पं. आनंद मिश्र', specialization: 'marriage-specialist', bio: 'Renowned for conducting traditional Vedic wedding ceremonies. Has officiated over 500 weddings with full Saptapadi and Kanyadaan rituals.', rating: 4.95, totalReviews: 524, experienceYears: 30, languages: ['Hindi', 'Sanskrit', 'Bhojpuri'], availableOnline: false, availableOffline: true, featured: true, isVerified: true, hourlyRate: 2500, totalPujasPerformed: 1800 },
  { id: 'p4', name: 'Pt. Vijay Pandey', nameHindi: 'पं. विजय पांडेय', specialization: 'astrology', bio: 'Jyotish Acharya with expertise in Kundali matching, Muhurt calculation, and planetary remedies. Available for consultation online and in-person.', rating: 4.7, totalReviews: 441, experienceYears: 25, languages: ['Hindi', 'English'], availableOnline: true, availableOffline: true, featured: false, isVerified: true, hourlyRate: 800, totalPujasPerformed: 3100 },
  { id: 'p5', name: 'Pt. Deepak Dubey', nameHindi: 'पं. दीपक दुबे', specialization: 'pitru-karma', bio: 'Expert in Shraddha, Pind Daan, and Pitru Tarpan ceremonies. Regularly performs rituals at Saryu Ghat for ancestral peace.', rating: 4.85, totalReviews: 267, experienceYears: 15, languages: ['Hindi', 'Sanskrit'], availableOnline: true, availableOffline: true, featured: false, isVerified: true, hourlyRate: 1100, totalPujasPerformed: 2100 },
  { id: 'p6', name: 'Pt. Krishna Upadhyay', nameHindi: 'पं. कृष्ण उपाध्याय', specialization: 'katha-vachak', bio: 'Celebrated Katha Vachak known for his melodious Ram Katha recitations. Has performed Katha at temples across India and in NRI communities.', rating: 4.92, totalReviews: 389, experienceYears: 20, languages: ['Hindi', 'Sanskrit', 'English'], availableOnline: true, availableOffline: true, featured: true, isVerified: true, hourlyRate: 3000, totalPujasPerformed: 1500 },
];

const initials = (n = '') => n.replace(/^Pt\.?\s*/i, '').split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';

function PanditCard({ pandit, t }) {
  return (
    <article className="card pandit-card">
      <div className="pandit-card__head">
        {pandit.profileImage
          ? <img className="pandit-card__avatar" src={pandit.profileImage} alt="" loading="lazy" />
          : <span className="pandit-card__avatar" aria-hidden="true">{initials(pandit.name)}</span>}
        <div style={{ minWidth: 0 }}>
          <div className="pandit-card__name">
            {pandit.name}
            {pandit.isVerified && <Icon name="badge-check" size={16} />}
          </div>
          {pandit.nameHindi && <div className="pandit-card__exp hindi">{pandit.nameHindi}</div>}
          {pandit.rating != null && <Rating value={pandit.rating} count={pandit.totalReviews} />}
        </div>
      </div>

      <div className="pandit-card__chips">
        <Badge variant="soft">{t(SPECIALIZATION_LABELS[pandit.specialization] || pandit.specialization)}</Badge>
        {pandit.featured && <Badge variant="gold">{t('Featured')}</Badge>}
        {pandit.availableOnline && <Badge variant="outline"><Icon name="video" size={13} />{t('Online')}</Badge>}
        {pandit.availableOffline && <Badge variant="outline"><Icon name="temple" size={13} />{t('At temple')}</Badge>}
      </div>

      {pandit.bio && <p className="info-card__desc">{pandit.bio}</p>}

      <ul className="place-card__meta">
        {pandit.experienceYears != null && <li><Icon name="award" size={14} /><span>{t('{{n}} years experience', { n: pandit.experienceYears })}</span></li>}
        {pandit.languages?.length > 0 && <li><Icon name="languages" size={14} /><span>{pandit.languages.join(', ')}</span></li>}
        {pandit.totalPujasPerformed != null && <li><Icon name="diya" size={14} /><span>{t('{{n}} pujas performed', { n: Number(pandit.totalPujasPerformed).toLocaleString('en-IN') })}</span></li>}
      </ul>

      <div className="pandit-card__foot">
        <div className="price">
          {pandit.hourlyRate ? <>₹{Number(pandit.hourlyRate).toLocaleString('en-IN')} <small>{t('/ hour')}</small></> : <small>{t('Price on request')}</small>}
        </div>
        <Button size="sm" to="/epuja" icon={<Icon name="arrow-right" size={14} />}>{t('Book puja')}</Button>
      </div>
    </article>
  );
}

export default function PanditList() {
  const { t } = useTranslation();
  const [pandits, setPandits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [availability, setAvailability] = useState('');

  const loadPandits = useCallback(async () => {
    try {
      setLoading(true);
      const filters = { limit: 50 };
      if (search) filters.search = search;
      if (specialization) filters.specialization = specialization;
      const data = await fetchAllPandits(filters);
      const list = data?.data || data || [];
      setPandits(Array.isArray(list) && list.length ? list : SAMPLE_PANDITS);
    } catch {
      setPandits(SAMPLE_PANDITS);
    } finally {
      setLoading(false);
    }
  }, [search, specialization]);

  useEffect(() => { loadPandits(); }, [loadPandits]);

  const filtered = pandits.filter((p) => {
    const q = search.toLowerCase();
    if (q && !(p.name?.toLowerCase().includes(q) || p.bio?.toLowerCase().includes(q))) return false;
    if (specialization && p.specialization !== specialization) return false;
    if (availability === 'online' && !p.availableOnline) return false;
    if (availability === 'offline' && !p.availableOffline) return false;
    return true;
  });

  return (
    <>
      <SEO
        title="Pandit Ji Booking"
        description="Book verified and experienced pandits for your sacred ceremonies in Ayodhya — online or at the temple."
        canonicalUrl="/pandits"
        keywords="pandit booking Ayodhya, vedic priest online, e-puja pandit, puja booking Ayodhya"
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Booking') }, { label: t('Pandit Ji') }]} title={t('Pandit Ji Booking')} subtitle={t('Experienced, verified pandits for every puja and sanskar')} />

      <div className="container listing-wrap">
        <div className="listing__toolbar">
          <div className="listing__tools" style={{ marginLeft: 0, width: '100%' }}>
            <label className="search">
              <Icon name="search" size={16} />
              <span className="sr-only">{t('Search pandits')}</span>
              <input type="search" placeholder={t('Search pandits…')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </label>
            <label className="sort">
              <Icon name="filter" size={15} />
              <span className="sr-only">{t('Specialization')}</span>
              <select value={specialization} onChange={(e) => setSpecialization(e.target.value)}>
                <option value="">{t('All Specializations')}</option>
                {Object.entries(SPECIALIZATION_LABELS).map(([key, label]) => <option key={key} value={key}>{t(label)}</option>)}
              </select>
            </label>
            <label className="sort">
              <Icon name="video" size={15} />
              <span className="sr-only">{t('Availability')}</span>
              <select value={availability} onChange={(e) => setAvailability(e.target.value)}>
                <option value="">{t('Any availability')}</option>
                <option value="online">{t('Available online')}</option>
                <option value="offline">{t('Available at temple')}</option>
              </select>
            </label>
          </div>
        </div>

        <div className="results-meta">
          <span>{loading ? t('Loading pandits…') : t('{{count}} pandits', { count: filtered.length })}</span>
        </div>

        {loading ? (
          <div className="card-grid card-grid--wide">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={300} radius="var(--r-lg)" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="namaste" title={t('No pandits found')} body={t('Try adjusting your search or specialization filter.')} action={<Button variant="outline" onClick={() => { setSearch(''); setSpecialization(''); setAvailability(''); }}>{t('Clear filters')}</Button>} />
        ) : (
          <div className="card-grid card-grid--wide">
            {filtered.map((p) => <PanditCard key={p.id} pandit={p} t={t} />)}
          </div>
        )}
      </div>
    </>
  );
}
